// Built-ins
import { useState, useEffect, useRef } from "react";

// Icons / Images
import { BiPlus } from "react-icons/bi";
import { AiOutlineWarning, AiOutlineCheckCircle } from "react-icons/ai";

import axios from "axios";

// Modules / Functions
import Swal from "sweetalert2";
import { Modal, Col, Form, Row } from "react-bootstrap";
import GuardedModal from "../../common/GuardedModal";
import { Button } from "semantic-ui-react";
import PhoneInput from "react-phone-input-2";
import 'react-phone-input-2/lib/bootstrap.css';

// Global Variables
import { headers } from "../../global_vars";
import AutocompleteSelect from "../../common/AutocompleteSelect";
import SelectWithInlineCreate from "../../common/SelectWithInlineCreate";
import AddProfessionModal from "./add_profession";
import AddProfessionalModal from "./add_professional";
import {
  formatPhoneForApi,
  isOptionalPhoneValid,
  isRequiredPhoneValid,
} from "../../../utils/phoneInput";
import RichTextEditor from "../../common/RichTextEditor";
import { htmlForApi } from "../../../utils/richText";

// Variables
window.Swal = Swal;

// API endpoint
const ADD_CLIENT_CONTACT = "https://ultima.icsgr.com/api/data_management/client_contacts/";

// Helpers
const clampLen = (value, max) => value.slice(0, max);

const validateEmail = (email) => {
  if (!email) return true; // Optional field
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

function digitsForPhoneInput(raw) {
  if (raw == null || raw === "") return "";
  return String(raw).replace(/\D/g, "");
}

function applyProfessionalToFormFields(prof, setters) {
  if (!prof) return;
  const {
    setFullname,
    setFathername,
    setMothername,
    setConnection,
    setAddress,
    setEmail,
    setPhone,
    setMobile,
    setProfession,
    setReliability,
    setCity,
    setNotes,
  } = setters;
  setFullname(prof.fullname || "");
  setFathername("");
  setMothername("");
  setConnection("");
  setAddress(prof.address || "");
  setEmail(prof.email || "");
  setPhone(digitsForPhoneInput(prof.phone));
  setMobile(digitsForPhoneInput(prof.mobile));
  const profTitle =
    prof.profession && (prof.profession.title || prof.profession.name || prof.profession);
  setProfession(typeof profTitle === "string" ? profTitle : "");
  setReliability(prof.reliability || "");
  const cityTitle =
    prof.city && (prof.city.title || prof.city.name || prof.city);
  setCity(typeof cityTitle === "string" ? cityTitle : "");
  setNotes(prof.notes || "");
}

function AddClientContactModal({ refreshData, defaultProjectId, lockProject = false, defaultProfessionalId, lockProfessional = false }) {
  const [show, setShow] = useState(false);

  // Basic Information
  const [fullname, setFullname] = useState("");
  const [fathername, setFathername] = useState("");
  const [mothername, setMothername] = useState("");
  const [connection, setConnection] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [mobile, setMobile] = useState("");
  const [profession, setProfession] = useState("");
  const [reliability, setReliability] = useState("");
  const [city, setCity] = useState("");
  
  // Foreign Keys
  const [projectId, setProjectId] = useState("");
  const [professionalId, setProfessionalId] = useState("");
  
  // System Information
  const [active, setActive] = useState(true);
  const [notes, setNotes] = useState("");

  // Dropdown Data
  const [projects, setProjects] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [addProfessionOpen, setAddProfessionOpen] = useState(false);
  const [addProfessionalOpen, setAddProfessionalOpen] = useState(false);
  const [lockedByProfessional, setLockedByProfessional] = useState(false);
  /** Avoid re-copying the same professional when `professionals` gets a new array reference (keeps user edits). */
  const lastHydratedProfessionalSig = useRef("");

  const resetForm = () => {
    setFullname("");
    setFathername("");
    setMothername("");
    setConnection("");
    setAddress("");
    setEmail("");
    setPhone("");
    setMobile("");
    setProfession("");
    setReliability("");
    setCity("");
    setProjectId("");
    setProfessionalId("");
    setActive(true);
    setNotes("");
  };

  useEffect(() => {
    if (show) {
      loadDropdownData();
    }
  }, [show]);

  // After professionals load (or ID changes), hydrate form — avoids race when opening with defaults
  // or selecting before the list request finishes.
  useEffect(() => {
    if (!show || !professionalId) {
      lastHydratedProfessionalSig.current = "";
      return;
    }
    const prof = professionals.find((p) => String(p.professional_id) === String(professionalId));
    if (!prof) return;
    const sig = [
      professionalId,
      prof.fullname || "",
      prof.phone || "",
      prof.mobile || "",
      prof.email || "",
      prof.address || "",
      prof.city && (prof.city.title || prof.city.name || ""),
      prof.profession && (prof.profession.title || prof.profession.name || ""),
      prof.notes || "",
    ].join("|");
    if (lastHydratedProfessionalSig.current === sig) return;
    lastHydratedProfessionalSig.current = sig;
    applyProfessionalToFormFields(prof, {
      setFullname,
      setFathername,
      setMothername,
      setConnection,
      setAddress,
      setEmail,
      setPhone,
      setMobile,
      setProfession,
      setReliability,
      setCity,
      setNotes,
    });
    setLockedByProfessional(true);
  }, [show, professionalId, professionals]);

  const loadDropdownData = async () => {
    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };
      const [projectsResponse, professionalsResponse, professionsResponse] = await Promise.all([
        axios.get("https://ultima.icsgr.com/api/data_management/all_projects/", { headers: currentHeaders }),
        axios.get("https://ultima.icsgr.com/api/data_management/professionals/", { headers: currentHeaders }),
        axios.get("https://ultima.icsgr.com/api/administration/all_professions/", { headers: currentHeaders }),
      ]);
      setProjects(projectsResponse?.data?.all_projects || []);
      setProfessionals(professionalsResponse?.data?.all_professionals || []);
      setProfessions(professionsResponse?.data?.all_professions || []);
    } catch (error) {
      console.error('Error loading dropdown data:', error);
    }
  };

  const handleClose = () => {
    lastHydratedProfessionalSig.current = "";
    setShow(false);
    resetForm();
  };

  const handleShow = () => {
    lastHydratedProfessionalSig.current = "";
    resetForm();
    if (defaultProjectId) setProjectId(defaultProjectId);
    if (defaultProfessionalId) setProfessionalId(defaultProfessionalId);
    setShow(true);
  };

  const onProfessionalSelect = (id) => {
    const nextId = id || "";
    lastHydratedProfessionalSig.current = "";
    setProfessionalId(nextId);
    if (!nextId) {
      setLockedByProfessional(false);
      return;
    }
    const prof = professionals.find((p) => String(p.professional_id) === String(nextId));
    if (!prof) {
      setLockedByProfessional(false);
      return;
    }
    applyProfessionalToFormFields(prof, {
      setFullname,
      setFathername,
      setMothername,
      setConnection,
      setAddress,
      setEmail,
      setPhone,
      setMobile,
      setProfession,
      setReliability,
      setCity,
      setNotes,
    });
    setLockedByProfessional(true);
  };

  // Validation
  const isFullnameValid = fullname.trim().length >= 2 && fullname.trim().length <= 40;
  const isAddressValid = !address || (address.trim().length >= 2 && address.trim().length <= 80);
  const isEmailValid = validateEmail(email);
  const normalizedPhone = formatPhoneForApi(phone) || "";
  const normalizedMobile = formatPhoneForApi(mobile) || "";
  const isPhoneValid = isOptionalPhoneValid(phone);
  const isMobileValid = isRequiredPhoneValid(mobile);
  const isProfessionValid = !profession || (profession.trim().length >= 2 && profession.trim().length <= 40);
  const isReliabilityValid = true; // optional
  const isCityValid = city.trim().length >= 2 && city.trim().length <= 40;
  const isProjectValid = projectId !== "";

  const isFormValid =
    isFullnameValid &&
    isAddressValid &&
    isEmailValid &&
    isPhoneValid &&
    isMobileValid &&
    isProfessionValid &&
    isCityValid &&
    isProjectValid;

  const handleSubmit = async () => {
    if (!isFormValid) {
      return;
    }

    const currentHeaders = {
      ...headers,
      "Authorization": "Token " + localStorage.getItem("userToken")
    };

    const clientContactData = {
      fullname: fullname.trim(),
      fathername: fathername.trim() || null,
      mothername: mothername.trim() || null,
      connection: connection.trim() || null,
      address: address.trim() || null,
      email: email.trim() || null,
      phone: normalizedPhone || null,
      mobile: normalizedMobile,
      profession: profession.trim() || null,
      reliability: reliability || null,
      city: city.trim(),
      project_id: projectId,
      professional_id: professionalId || null,
      active,
      notes: htmlForApi(notes),
    };

    try {
      await axios.post(
        ADD_CLIENT_CONTACT,
        clientContactData,
        { headers: currentHeaders }
      );

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Client contact created successfully!",
      });

      handleClose();
      
      if (refreshData) {
        refreshData();
      }
    } catch (error) {
      console.error("Error creating client contact:", error);
      const data = error.response?.data;
      let msg = data?.error || data?.detail;
      if (!msg && data && typeof data === "object") {
        const parts = Object.entries(data).map(([k, v]) => {
          const s = Array.isArray(v) ? v.join(" ") : String(v);
          return `${k}: ${s}`;
        });
        if (parts.length) msg = parts.join("\n");
      }
      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg || "Failed to create client contact.",
      });
    }
  };

  return (
    <>
      <Button color="green" style={{ margin: 20 }} onClick={handleShow}>
        <BiPlus
          style={{ color: "white", fontSize: "1.3em", marginRight: "0.3em" }}
        />
        Create new Client Contact
      </Button>

      <GuardedModal
        show={show}
        size="xl"
        onHide={handleClose}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Create new Client Contact</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="justify-content-md-center">
            <Col>
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Full name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={fullname}
                        onChange={(e) => setFullname(clampLen(e.target.value, 40))}
                        maxLength={40}
                        placeholder="Enter full name"
                        disabled={lockedByProfessional}
                      />
                      <Form.Text className="text-muted">
                        {fullname.length}/40 characters
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Father fullname</Form.Label>
                      <Form.Control
                        type="text"
                        value={fathername}
                        onChange={(e) => setFathername(clampLen(e.target.value, 80))}
                        maxLength={80}
                        placeholder="Enter father's name"
                      />
                      <Form.Text className="text-muted">
                        {fathername.length}/80 characters
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Mother fullname</Form.Label>
                      <Form.Control
                        type="text"
                        value={mothername}
                        onChange={(e) => setMothername(clampLen(e.target.value, 80))}
                        maxLength={80}
                        placeholder="Enter mother's name"
                      />
                      <Form.Text className="text-muted">
                        {mothername.length}/80 characters
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Connection</Form.Label>
                      <Form.Control
                        type="text"
                        value={connection}
                        onChange={(e) => setConnection(clampLen(e.target.value, 40))}
                        maxLength={40}
                        placeholder="Enter connection type"
                      />
                      <Form.Text className="text-muted">
                        {connection.length}/40 characters
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>City *</Form.Label>
                      <Form.Control
                        type="text"
                        value={city}
                        onChange={(e) => setCity(clampLen(e.target.value, 40))}
                        maxLength={40}
                        placeholder="Enter city"
                        disabled={lockedByProfessional}
                      />
                      <Form.Text className="text-muted">
                        {city.length}/40 characters
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Address *</Form.Label>
                      <Form.Control
                        type="text"
                        value={address}
                        onChange={(e) => setAddress(clampLen(e.target.value, 80))}
                        maxLength={80}
                        placeholder="Enter address"
                        disabled={lockedByProfessional}
                      />
                      <Form.Text className="text-muted">
                        {address.length}/80 characters
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>E-mail *</Form.Label>
                      <Form.Control
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter email address"
                        disabled={lockedByProfessional}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <SelectWithInlineCreate
                      className="mb-1"
                      label="Profession"
                      value={profession}
                      onChange={setProfession}
                      disabled={lockedByProfessional}
                      placeholderOption="Select profession"
                      onAddClick={
                        lockedByProfessional ? undefined : () => setAddProfessionOpen(true)
                      }
                    >
                      {Array.isArray(professions) &&
                        professions.map((p) => (
                          <option key={p.profession_id} value={p.title} title={p.profession_id}>
                            {p.title}
                          </option>
                        ))}
                      {profession &&
                      !professions.some((p) => p.title === profession) ? (
                        <option value={profession}>{profession}</option>
                      ) : null}
                    </SelectWithInlineCreate>
                    <Form.Text className="text-muted d-block mb-3">
                      Job titles from Administration → Professions. Use New to add a category.
                    </Form.Text>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Telephone *</Form.Label>
                      <PhoneInput
                        country={'gr'}
                        value={phone}
                        onChange={(val) => setPhone(val)}
                        enableSearch
                        countryCodeEditable={false}
                        inputProps={{ name: 'phone' }}
                        disabled={lockedByProfessional}
                      />
                      <Form.Text className="text-muted">
                        {phone.length}/16 characters
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cell phone *</Form.Label>
                      <PhoneInput
                        country={'gr'}
                        value={mobile}
                        onChange={(val) => setMobile(val)}
                        enableSearch
                        countryCodeEditable={false}
                        inputProps={{ name: 'mobile' }}
                        disabled={lockedByProfessional}
                      />
                      <Form.Text className="text-muted">
                        {mobile.length}/16 characters
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Reliability</Form.Label>
                      <AutocompleteSelect
                        value={reliability}
                        onChange={setReliability}
                        disabled={lockedByProfessional}
                        placeholder="Select reliability level"
                      >
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </AutocompleteSelect>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Project *</Form.Label>
                      <AutocompleteSelect
                        value={projectId}
                        onChange={setProjectId}
                        disabled={lockProject}
                        placeholder="Select project"
                      >
                        {projects.map((project) => (
                          <option key={project.project_id} value={project.project_id}>
                            {project.title}
                          </option>
                        ))}
                      </AutocompleteSelect>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <SelectWithInlineCreate
                      className="mb-1"
                      label="Professional"
                      value={professionalId}
                      onChange={onProfessionalSelect}
                      disabled={lockProfessional}
                      placeholderOption="Select professional (optional)"
                      onAddClick={
                        lockProfessional ? undefined : () => setAddProfessionalOpen(true)
                      }
                      overviewLinkEntity="professional"
                    >
                      {professionals.map((professional) => (
                        <option
                          key={professional.professional_id}
                          value={professional.professional_id}
                          title={professional.professional_id}
                        >
                          {professional.fullname}
                          {professional.profession?.title
                            ? ` — ${professional.profession.title}`
                            : ""}
                        </option>
                      ))}
                    </SelectWithInlineCreate>
                    <Form.Text className="text-muted d-block mb-3">
                      Use New to create a professional so they appear in Data Management → Professionals.
                    </Form.Text>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Active"
                        checked={active}
                        onChange={(e) => setActive(e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Notes</Form.Label>
                      <RichTextEditor
                        rows={3}
                        value={notes}
                        onChange={setNotes}
                        placeholder="Enter notes"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto">
            {!isFormValid ? (
              <ul
                className="mr-auto"
                style={{ margin: 0, padding: 0, color: "red" }}
              >
                {!isFullnameValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Full name is required (2–40 chars).
                  </li>
                )}
                {!isAddressValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Address is required (2–80 chars).
                  </li>
                )}
                {!isEmailValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Valid e-mail is required.
                  </li>
                )}
                {!isPhoneValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Telephone must be at least 7 digits when provided.
                  </li>
                )}
                {!isMobileValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Cell phone is required and must be at least 7 digits.
                  </li>
                )}
                {!isProfessionValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Profession is required (2–40 chars).
                  </li>
                )}
                {!isReliabilityValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Reliability is required.
                  </li>
                )}
                {!isCityValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    City is required (2–40 chars).
                  </li>
                )}
                {!isProjectValid && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Project is required.
                  </li>
                )}
              </ul>
            ) : (
              <ul
                className="mr-auto"
                style={{ margin: 0, padding: 0, color: "green" }}
              >
                <li>
                  <AiOutlineCheckCircle
                    style={{ fontSize: 18, marginRight: 6 }}
                  />
                  Validated
                </li>
              </ul>
            )}
          </small>

          <Button color="red" onClick={handleClose}>
            Close
          </Button>
          <Button
            color="green"
            onClick={handleSubmit}
            disabled={!isFormValid}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </GuardedModal>

      <AddProfessionModal
        nested
        hideTrigger
        show={addProfessionOpen}
        onHide={() => setAddProfessionOpen(false)}
        onProfessionCreated={async (data) => {
          await loadDropdownData();
          const title = data?.title;
          if (title) setProfession(title);
          setAddProfessionOpen(false);
        }}
      />
      <AddProfessionalModal
        nested
        hideTrigger
        show={addProfessionalOpen}
        onHide={() => setAddProfessionalOpen(false)}
        onProfessionalCreated={async (data) => {
          await loadDropdownData();
          const id = data?.professional_id;
          if (id) {
            lastHydratedProfessionalSig.current = "";
            setProfessionalId(id);
          }
          setAddProfessionalOpen(false);
        }}
      />
    </>
  );
}

export default AddClientContactModal;
